// AssetBudgetBadge — a compact memory-budget flag for the asset detail
// header (roadmap D-6). Shows only when an asset is over/near budget, so
// within-budget assets stay quiet. Textures are judged by authored
// resolution, meshes by triangle count (see budget.ts).

import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';
import { api } from '../api.js';
import {
  isMeshClass,
  isTextureClass,
  meshVerdict,
  textureSourceOf,
  textureVerdict,
  verdictLabel,
  verdictTone,
  type BudgetVerdict,
} from './budget.js';

export function AssetBudgetBadge({
  depotPath,
  rev,
  assetClass,
}: {
  depotPath: string;
  rev: number;
  assetClass: string | null;
}) {
  const isTexture = isTextureClass(assetClass);
  const isMesh = isMeshClass(assetClass);

  const q = useQuery({
    // Textures read the properties JSON (authored Source dims); meshes
    // read meta.json (glbStats). Disabled for everything else.
    queryKey: ['dam.budget', depotPath, rev, isTexture ? 'tex' : 'mesh'],
    enabled: isTexture || isMesh,
    queryFn: () => (isTexture ? fetchPreviewJson(depotPath, rev) : fetchMeta(depotPath, rev)),
  });

  if (!isTexture && !isMesh) return null;
  if (!q.data) return null;

  let verdict: BudgetVerdict;
  let detail: string;
  if (isTexture) {
    const src = textureSourceOf(q.data);
    if (!src) return null;
    const maxDim = Math.max(src.sizeX, src.sizeY);
    verdict = textureVerdict(maxDim);
    detail = `Texture ${src.sizeX}×${src.sizeY}${src.format ? ` (${src.format})` : ''}`;
  } else {
    const tris = meshTriangles(q.data);
    if (tris === null) return null;
    verdict = meshVerdict(tris);
    detail = `Mesh ${tris.toLocaleString()} triangles`;
  }

  if (verdict === 'ok') return null; // stay quiet when within budget

  return (
    <Tooltip label={`${detail} — ${verdictLabel(verdict)}`}>
      <span>
        <Badge tone={verdictTone(verdict)}>
          {verdict === 'over' ? '⚠ over budget' : 'large'}
        </Badge>
      </span>
    </Tooltip>
  );
}

function meshTriangles(meta: unknown): number | null {
  if (typeof meta !== 'object' || meta === null) return null;
  const stats = (meta as Record<string, unknown>).glbStats;
  if (typeof stats !== 'object' || stats === null) return null;
  const t = (stats as Record<string, unknown>).triangleCount;
  return typeof t === 'number' && Number.isFinite(t) ? t : null;
}

async function fetchMeta(depotPath: string, rev: number): Promise<unknown | null> {
  const res = await fetch(`/api/dam/meta?path=${encodeURIComponent(depotPath)}&rev=${rev}`, {
    credentials: 'include',
  });
  if (res.status !== 200) return null;
  return res.json();
}

async function fetchPreviewJson(depotPath: string, rev: number): Promise<unknown | null> {
  const res = await fetch(api.damPreviewUrl(depotPath, rev, 'json'), { credentials: 'include' });
  if (res.status !== 200) return null;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('json')) return null;
  const body = await res.json();
  if (typeof body === 'object' && body && 'noPreview' in body) return null;
  return body;
}
